import React from "react";

import {
  border,
  BorderProps,
  color,
  ColorProps,
  compose,
  EmotionComponent,
  FocusBorderColorProp,
  focusBorderColorProp,
  HoverColorProp,
  hoverColorProp,
  layout,
  LayoutProps,
  OverrideNativeElementProps,
  space,
  SpaceProps,
  styled,
  themeGet,
} from "@catchoftheday/theme";

import { Flex, FlexProps } from "../Flex";
import { Hide, HideProps } from "../Hide";
import { IconDownArrow, IconProps } from "../Icon";

type BaseSelectProps = OverrideNativeElementProps<
  HoverColorProp &
    FocusBorderColorProp &
    LayoutProps &
    BorderProps &
    SpaceProps &
    ColorProps,
  "select"
>;

export type SelectProps = {
  /** props for the container, if the consumer want the component to be full width, it need to set width property rather than display property */
  containerProps?: FlexProps;
  /** prefix icon */
  prefixIcon?: React.FC<IconProps>;
  /** prefix icon colour */
  prefixIconColour?: string;
  /** icon ratio width/height */
  iconRatio?: number;
  /** hide drop down icon */
  dropDownIconHideProps?: HideProps;
} & BaseSelectProps;

const styleProps = compose(layout, border, space, color);

const BaseSelect: EmotionComponent<BaseSelectProps> = styled.select<BaseSelectProps>`
  padding-right: ${themeGet("space.5")};
  color: inherit;
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
  appearance: none;
  -ms-appearance: none; /* Edge */
  cursor: pointer;
  vertical-align: middle;
  /** IE11 */
  &::-ms-expand {
    display: none;
  }

  &:focus {
    outline: 0;
    border-radius: 0;
    ${focusBorderColorProp}
  }

  &:hover:enabled {
    ${hoverColorProp}
    ~ span > svg {
      ${hoverColorProp}
    }
  }

  ${(prop) =>
    prop.maxWidth &&
    `
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;`}

  ${styleProps}
`;

BaseSelect.defaultProps = {
  py: 3,
  border: 1,
  borderRadius: "4px",
  borderColor: "ui.borderColor",
  focusBorderColor: "secondary",
};

const IconWrapperAfter = styled.span`
  position: absolute;
  top: calc(50% - 0.75em);
  right: 3px;
  padding-right: 10px;
  pointer-events: none;
`;

const IconWrapperBefore = styled.span`
  position: absolute;
  top: calc(50% - 0.75em);
  left: 0.75em;
`;

export const Select: React.FC<SelectProps> = React.forwardRef(
  (
    {
      containerProps,
      prefixIcon: PrefixIcon,
      prefixIconColour,
      iconRatio,
      required = false,
      disabled = false,
      dropDownIconHideProps,
      children,
      ...otherProps
    },
    ref
  ) => {
    return (
      <Flex
        display="inline-flex"
        position="relative"
        alignItems="center"
        {...containerProps}
      >
        <BaseSelect
          ref={ref}
          aria-required={required}
          aria-disabled={disabled}
          required={required}
          disabled={disabled}
          width={containerProps?.width ? "100%" : "auto"}
          pl={PrefixIcon ? "2.5rem" : "0.75rem"}
          pr="35px"
          color="secondary"
          bg="white"
          {...otherProps}
        >
          {children}
        </BaseSelect>
        <Hide {...dropDownIconHideProps}>
          <IconWrapperAfter>
            <IconDownArrow
              width="0.8em"
              height="0.4em"
              color={disabled ? "typography.inactive" : "inherit"}
              title="select drop down arrow"
            />
          </IconWrapperAfter>
        </Hide>
        {PrefixIcon && (
          <IconWrapperBefore>
            {
              <PrefixIcon
                height="1.5em"
                width={`${1.5 * (iconRatio || 0.5)}em`}
                color={
                  disabled
                    ? "typography.inactive"
                    : prefixIconColour || "inherit"
                }
              />
            }
          </IconWrapperBefore>
        )}
      </Flex>
    );
  }
);

Select.displayName = "Select";
Select.defaultProps = {
  containerProps: {
    color: "typography.text",
  },
  dropDownIconHideProps: {},
};

export default Select;
